import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useCommunityData } from '@/contexts/DataContext';
import type { Community, User } from '@/types';


export interface ActiveMembershipProfile {
  communityId: string;
  role?: User['role'];
  block?: string | null;
  floor?: string | null;
  letter?: string | null;
  isDefault?: boolean;
}

const hasValidId = (community: Community | null | undefined): community is Community => {
  return Boolean(community && community.id);
};

const normalizeCommunityKey = (value: string): string => value.trim();

export interface CommunityScopeValue {
  selectedCommunityId: string | null;
  selectedCommunityIds: string[];
  setSelectedCommunityId: (communityId: string) => void;
  setSelectedCommunityIds: (communityIds: string[]) => void;
  canChangeCommunity: boolean;
  availableCommunities: Community[];
  isGlobalAdmin: boolean;
  /**
   * Conjunto de comunidades activas para filtrar datos. Para administradores globales
   * coincide con la selección manual (puede contener varias comunidades); para el resto
   * de usuarios se limita a la comunidad asignada en su perfil.
   */
  activeCommunityIds: string[];
  /**
   * Comunidad actualmente activa para aquellas vistas que trabajan con una única comunidad.
   * Cuando se seleccionan varias comunidades, este valor pasa a ser `null` para indicar
   * que no hay un único contexto activo.
   */
  activeCommunityId: string | null;
  /**
   * Referencia directa a la comunidad activa cuando solo hay una seleccionada. Puede ser
   * `null` cuando el usuario aún no tiene comunidades disponibles o ha seleccionado varias.
   */
  activeCommunity: Community | null;
  /**
   * Lista completa de comunidades activas. Para administradores globales refleja toda la
   * selección manual; para el resto de usuarios contendrá como máximo una comunidad.
   */
  activeCommunities: Community[];
  activeMembershipProfile: ActiveMembershipProfile | null;
}

const CommunityScopeContext = React.createContext<CommunityScopeValue | undefined>(undefined);

function useCommunityScopeState(): CommunityScopeValue {
  const { currentUser } = useAuth();
  const { communities } = useCommunityData();

  const hasScopedMemberships = Array.isArray(currentUser?.communityIds) && currentUser.communityIds.length > 0;
  const isGlobalAdmin = Boolean(currentUser?.role === 'admin' && !hasScopedMemberships);

  const availableCommunities = React.useMemo(() => {
    const seen = new Set<string>();
    const unique: Community[] = [];
    communities
      .filter(hasValidId)
      .forEach((community) => {
        const normalizedId = normalizeCommunityKey(String(community.id));
        if (!normalizedId || seen.has(normalizedId)) {
          return;
        }
        seen.add(normalizedId);
        unique.push({ ...community, id: normalizedId });
      });
    return unique;
  }, [communities]);

  const areArraysEqual = React.useCallback((a: string[], b: string[]) => {
    if (a === b) return true;
    if (a.length !== b.length) return false;
    return a.every((value, index) => value === b[index]);
  }, []);

  const normalizeId = React.useCallback((value?: string | null) => {
    if (typeof value !== 'string') return '';
    return value.trim();
  }, []);

  const sanitizeCommunityIds = React.useCallback(
    (ids: string[]) => {
      if (!ids.length) {
        return [];
      }

      const availableIds = new Set(availableCommunities.map(community => normalizeCommunityKey(community.id)));
      const uniqueIds = Array.from(new Set(ids));
      return uniqueIds
        .map(id => normalizeCommunityKey(id))
        .filter(id => availableIds.has(id));
    },
    [availableCommunities]
  );

  const [selectedCommunityIds, setSelectedCommunityIdsState] = React.useState<string[]>(() => {
    if (!availableCommunities.length) {
      return [];
    }

    if (isGlobalAdmin) {
      return [];
    }

    const membershipDefaultId = normalizeId(
      currentUser?.communityMemberships?.find(membership => membership.isDefault)?.communityId ?? null
    );
    const userCommunityIds = (currentUser?.communityIds ?? []).filter(id => availableCommunities.some(community => community.id === id));
    if (membershipDefaultId && userCommunityIds.includes(membershipDefaultId)) {
      return [membershipDefaultId];
    }
    if (userCommunityIds.length > 0) {
      return [userCommunityIds[0]];
    }

    return [availableCommunities[0].id];
  });

  const selectedCommunityId = selectedCommunityIds[0] ?? null;

  React.useEffect(() => {
    setSelectedCommunityIdsState(prev => {
      if (!availableCommunities.length) {
        return prev.length ? [] : prev;
      }

      const sanitized = sanitizeCommunityIds(prev);

      if (!isGlobalAdmin) {
        const membershipDefaultId = normalizeId(
          currentUser?.communityMemberships?.find(membership => membership.isDefault)?.communityId ?? null
        );
        const userCommunityIds = (currentUser?.communityIds ?? []).filter(id => availableCommunities.some(community => community.id === id));
        if (userCommunityIds.length > 0) {
          const candidate = sanitized.find(id => userCommunityIds.includes(id));
          const fallbackId = (membershipDefaultId && userCommunityIds.includes(membershipDefaultId))
            ? membershipDefaultId
            : userCommunityIds[0];
          const next = [candidate ?? fallbackId];
          return areArraysEqual(prev, next) ? prev : next;
        }

        const fallback = sanitized[0] ?? availableCommunities[0].id;
        const next = fallback ? [fallback] : [];
        return areArraysEqual(prev, next) ? prev : next;
      }

      return areArraysEqual(prev, sanitized) ? prev : sanitized;
    });
  }, [availableCommunities, currentUser?.communityIds, currentUser?.communityMemberships, isGlobalAdmin, areArraysEqual, normalizeId, sanitizeCommunityIds]);

  const handleSelectCommunity = React.useCallback((communityId: string) => {
    if (!communityId) {
      return;
    }

    setSelectedCommunityIdsState(prev => {
      if (prev.length === 1 && prev[0] === communityId) {
        return prev;
      }
      return [communityId];
    });
  }, []);

  const handleSetSelectedCommunityIds = React.useCallback((communityIds: string[]) => {
    setSelectedCommunityIdsState(prev => {
      const next = sanitizeCommunityIds(communityIds);

      if (!isGlobalAdmin) {
        const userCommunityIds = (currentUser?.communityIds ?? []).filter(Boolean);
        const candidate = next.find(id => userCommunityIds.includes(id));
        if (userCommunityIds[0]) {
          const normalized = [candidate ?? userCommunityIds[0]];
          return areArraysEqual(prev, normalized) ? prev : normalized;
        }

        const fallback = next[0] ?? null;
        if (!fallback) {
          return prev;
        }
        const normalized = [fallback];
        return areArraysEqual(prev, normalized) ? prev : normalized;
      }

      return areArraysEqual(prev, next) ? prev : next;
    });
  }, [areArraysEqual, currentUser?.communityIds, isGlobalAdmin, sanitizeCommunityIds]);

  const canChangeCommunity = isGlobalAdmin
    ? availableCommunities.length > 0
    : availableCommunities.length > 1 && ((currentUser?.communityIds?.length ?? 0) > 1);

  const activeCommunityId = React.useMemo(() => {
    if (isGlobalAdmin) {
      return selectedCommunityIds.length === 1 ? selectedCommunityIds[0] : null;
    }

    return selectedCommunityId;
  }, [isGlobalAdmin, selectedCommunityId, selectedCommunityIds]);

  const activeCommunityIds = React.useMemo(() => {
    if (isGlobalAdmin) {
      return selectedCommunityIds;
    }

    return selectedCommunityId ? [selectedCommunityId] : [];
  }, [isGlobalAdmin, selectedCommunityId, selectedCommunityIds]);

  const activeCommunity = React.useMemo(() => {
    if (!activeCommunityId) {
      return null;
    }

    return availableCommunities.find(community => community.id === activeCommunityId) ?? null;
  }, [activeCommunityId, availableCommunities]);

  const activeMembershipProfile = React.useMemo(() => {
    if (!activeCommunityId) return null;
    const memberships = currentUser?.communityMemberships ?? [];
    const targetCommunityId = normalizeId(activeCommunityId);
    const matches = memberships.filter(m => normalizeId(m.communityId) === targetCommunityId);
    if (!matches.length) return null;

    // Priorizamos filas completas y por defecto para evitar tomar registros parciales/duplicados
    // en `user_roles_by_community` que pueden dejar `role` vacío y romper permisos del sidebar.
    const best = [...matches].sort((a, b) => {
      const score = (membership: ActiveMembershipProfile) => {
        let value = 0;
        if (membership.isDefault) value += 4;
        if (membership.role) value += 2;
        if (membership.block || membership.floor || membership.letter) value += 1;
        return value;
      };
      return score(b) - score(a);
    })[0];

    return best ?? null;
  }, [activeCommunityId, currentUser?.communityMemberships, normalizeId]);

  const activeCommunities = React.useMemo(() => {
    if (!activeCommunityIds.length) {
      return [];
    }

    const activeSet = new Set(activeCommunityIds);
    return availableCommunities.filter(community => activeSet.has(community.id));
  }, [activeCommunityIds, availableCommunities]);

  return {
    selectedCommunityId,
    selectedCommunityIds,
    setSelectedCommunityId: handleSelectCommunity,
    setSelectedCommunityIds: handleSetSelectedCommunityIds,
    canChangeCommunity,
    availableCommunities,
    isGlobalAdmin,
    activeCommunityIds,
    activeCommunityId,
    activeCommunity,
    activeCommunities,
    activeMembershipProfile,
  };
}

export const CommunityScopeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const value = useCommunityScopeState();

  return (
    <CommunityScopeContext.Provider value={value}>
      {children}
    </CommunityScopeContext.Provider>
  );
};

export function useCommunityScope(): CommunityScopeValue {
  const context = React.useContext(CommunityScopeContext);

  if (!context) {
    throw new Error('useCommunityScope must be used within a CommunityScopeProvider');
  }

  return context;
}
